/**
 * @file components/staff-profile/BusinessCategoryTab.tsx
 * @description 驗證業務核准類別分頁。
 *
 * 功能：
 *  - 展示員工的驗證業務核准類別清單（方案/類別/核准日期/到期日）
 *  - 序53：新增 Modal 業務類別「可多選」（Select mode="multiple"），一次批量新增多個類別；
 *    編輯 Modal 仍為單一類別（PATCH 語義）。
 *  - 刪除（Popconfirm）
 *  端點：POST/PATCH/DELETE /api/settings/staff/:id/business-categories[/:bid]
 */

import { useState } from 'react';
import { Button, DatePicker, Form, Modal, Popconfirm, Select, Space, Table, Tag } from 'antd';
import { message } from '@/lib/antd-app';
import { Plus, Edit3, Trash2, CheckSquare } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { SCHEME_LABELS, VERIFICATION_SCHEMES } from '@zhcb/shared';
import type { VerificationScheme } from '@zhcb/shared';
import { apiClient } from '@/lib/api-client';
import { SectionCard } from '@/components/shell';
import { useVerificationCategories } from '@/hooks/useClientLookups';
import type { ColumnsType } from 'antd/es/table';

// ─── 型別 ─────────────────────────────────────────────────────────────────────

/** 驗證業務核准類別記錄（後端回傳） */
export interface BusinessCategory {
  id: number;
  scheme: VerificationScheme;
  category: string;
  approvedAt?: string | null;
  expiresAt?: string | null;
}

/**
 * 表單值。
 * 序53：新增態 category 為多選陣列（string[]）；編輯態為單一字串（string）。
 * 用聯合型別涵蓋兩種模式，提交時再依 editing 分流。
 */
interface CategoryFormValues {
  scheme: VerificationScheme;
  category: string | string[];
  approvedAt?: dayjs.Dayjs | null;
  expiresAt?: dayjs.Dayjs | null;
}

// ─── 組件 Props ───────────────────────────────────────────────────────────────

export interface BusinessCategoryTabProps {
  /** 員工 userId */
  userId: number;
  /** 業務核准類別列表（來自 profile 查詢） */
  businessCategories: BusinessCategory[];
}

// ─── 主組件 ──────────────────────────────────────────────────────────────────

/**
 * 驗證業務核准類別 Tab 主組件。
 */
export function BusinessCategoryTab({ userId, businessCategories }: BusinessCategoryTabProps) {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<BusinessCategory | null>(null);
  const [catForm] = Form.useForm<CategoryFormValues>();

  // 監聽選中的方案，用於過濾類別下拉選項
  const selectedScheme = Form.useWatch('scheme', catForm) as VerificationScheme | undefined;

  /** 驗證類別字典（activeOnly，可按 scheme 前端過濾） */
  const { data: categoriesData } = useVerificationCategories({ activeOnly: true });

  /** 依所選方案過濾類別選項（scheme=null 表示通用，顯示在所有方案下） */
  const filteredCategories = (categoriesData?.items ?? []).filter(
    (item) => !selectedScheme || !item.scheme || item.scheme === selectedScheme,
  );

  /**
   * 新增類別（序53：支援一次多選多個業務類別，逐筆呼叫既有 POST 端點）。
   * 對每個選中的 category 送一筆 POST，共用同一方案/核准日期/到期日。
   */
  const createCat = useMutation({
    mutationFn: async (values: CategoryFormValues) => {
      // 統一成陣列：多選為 string[]，容錯單值 string
      const categories = Array.isArray(values.category)
        ? values.category
        : [values.category].filter(Boolean);
      const approvedAt = values.approvedAt?.format('YYYY-MM-DD') ?? null;
      const expiresAt = values.expiresAt?.format('YYYY-MM-DD') ?? null;
      // 逐筆新增（後端為逐筆端點）；任一失敗即拋出，由 onError 呈現
      for (const category of categories) {
        await apiClient.post(`/api/settings/staff/${userId}/business-categories`, {
          scheme: values.scheme,
          category,
          approvedAt,
          expiresAt,
        });
      }
      return categories.length;
    },
    onSuccess: (count) => {
      message.success(`已新增 ${count} 項業務核准類別`);
      void qc.invalidateQueries({ queryKey: ['staff-profile', userId] });
      closeModal();
    },
    onError: (err) => {
      message.error(err instanceof Error ? err.message : '新增失敗');
    },
  });

  /** 更新類別 */
  const updateCat = useMutation({
    mutationFn: (values: CategoryFormValues) =>
      apiClient.patch(`/api/settings/staff/${userId}/business-categories/${editing!.id}`, {
        scheme: values.scheme,
        // 編輯態為單一類別；容錯多選陣列時取第一個
        category: Array.isArray(values.category) ? values.category[0] : values.category,
        approvedAt: values.approvedAt?.format('YYYY-MM-DD') ?? null,
        expiresAt: values.expiresAt?.format('YYYY-MM-DD') ?? null,
      }),
    onSuccess: () => {
      message.success('業務核准類別已更新');
      void qc.invalidateQueries({ queryKey: ['staff-profile', userId] });
      closeModal();
    },
    onError: (err) => {
      message.error(err instanceof Error ? err.message : '更新失敗');
    },
  });

  /** 刪除類別 */
  const deleteCat = useMutation({
    mutationFn: (bid: number) =>
      apiClient.delete(`/api/settings/staff/${userId}/business-categories/${bid}`),
    onSuccess: () => {
      message.success('業務核准類別已刪除');
      void qc.invalidateQueries({ queryKey: ['staff-profile', userId] });
    },
    onError: (err) => {
      message.error(err instanceof Error ? err.message : '刪除失敗');
    },
  });

  function openAdd() {
    setEditing(null);
    catForm.resetFields();
    setModalOpen(true);
  }

  function openEdit(record: BusinessCategory) {
    setEditing(record);
    catForm.setFieldsValue({
      scheme: record.scheme,
      category: record.category,
      approvedAt: record.approvedAt ? dayjs(record.approvedAt) : null,
      expiresAt: record.expiresAt ? dayjs(record.expiresAt) : null,
    });
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditing(null);
    catForm.resetFields();
  }

  /** 方案標籤色對應 */
  const schemeColor: Record<VerificationScheme, string> = {
    TOP: 'green',
    TAP: 'blue',
    ECO: 'cyan',
  };

  const columns: ColumnsType<BusinessCategory> = [
    {
      title: '驗證方案',
      dataIndex: 'scheme',
      width: 180,
      render: (v: VerificationScheme) => (
        <Tag color={schemeColor[v] ?? 'default'}>{SCHEME_LABELS[v] ?? v}</Tag>
      ),
    },
    {
      title: '核准業務類別',
      dataIndex: 'category',
      ellipsis: true,
    },
    {
      title: '核准日期',
      dataIndex: 'approvedAt',
      width: 110,
      // 格式化 ISO timestamp 為 YYYY-MM-DD 顯示
      render: (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD') : '—'),
    },
    {
      title: '到期日期',
      dataIndex: 'expiresAt',
      width: 110,
      // 格式化 ISO timestamp 為 YYYY-MM-DD 顯示
      render: (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD') : '—'),
    },
    {
      title: '操作',
      key: '_actions',
      width: 90,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<Edit3 size={13} />}
            onClick={() => openEdit(record)}
          />
          <Popconfirm
            title="確定刪除此業務類別記錄？"
            onConfirm={() => deleteCat.mutate(record.id)}
            okText="刪除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" size="small" danger icon={<Trash2 size={13} />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <SectionCard
        title="驗證業務核准類別"
        icon={<CheckSquare size={14} />}
        padless
        actions={
          <Button type="primary" size="small" icon={<Plus size={13} />} onClick={openAdd}>
            新增類別
          </Button>
        }
      >
        <Table<BusinessCategory>
          rowKey="id"
          size="small"
          dataSource={businessCategories}
          columns={columns}
          pagination={false}
          scroll={{ x: 600 }}
        />
      </SectionCard>

      {/* 新增/編輯 Modal */}
      <Modal
        title={editing ? '編輯業務核准類別' : '新增業務核准類別'}
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        width={480}
        destroyOnHidden
      >
        <Form<CategoryFormValues>
          form={catForm}
          layout="vertical"
          style={{ marginTop: 16 }}
          onFinish={(values) => (editing ? updateCat.mutate(values) : createCat.mutate(values))}
        >
          {/* 驗證方案 */}
          <Form.Item
            name="scheme"
            label="驗證方案"
            rules={[{ required: true, message: '請選擇驗證方案' }]}
          >
            <Select
              placeholder="請選擇方案（TOP / TAP / ECO）"
              options={VERIFICATION_SCHEMES.map((s) => ({
                value: s,
                label: SCHEME_LABELS[s],
              }))}
            />
          </Form.Item>

          {/* 業務類別（依選擇的方案過濾）。序53：新增態可多選，編輯態單選 */}
          <Form.Item
            name="category"
            label="業務類別"
            rules={[{ required: true, message: '請選擇業務類別' }]}
          >
            <Select
              // 編輯態單選（PATCH 針對單筆）；新增態多選批量新增
              mode={editing ? undefined : 'multiple'}
              showSearch
              allowClear
              placeholder={editing ? '請選擇業務類別' : '可選擇多個業務類別'}
              filterOption={(input, opt) =>
                ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={filteredCategories.map((item) => ({
                value: item.name,
                label: item.name,
              }))}
            />
          </Form.Item>

          {/* 核准日期 */}
          <Form.Item name="approvedAt" label="核准日期">
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>

          {/* 到期日期 */}
          <Form.Item name="expiresAt" label="到期日期">
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={createCat.isPending || updateCat.isPending}
            >
              {editing ? '儲存' : '新增'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
